import React, { useEffect, useState, useRef, useMemo, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { BASE_URL, restrictSpecialCharactersMultilingual } from "@utils/constants";
import { toast } from "react-toastify";
import apiClient from "@/services/api/apiClient";
import { useNavigate } from "react-router-dom";

// New component for the validated text cell
const ValidatedTextCell = React.forwardRef(
  (
    {
      sentenceId,
      initialValue,
      fileStatus,
      isFileAlreadyValidated,
      finalCommitCompleted,
      setTranslations,
      setEditedSentences,
      translationsStateRef,
      editedStateRef,
      setPopup,
    },
    ref
  ) => {
    const [localValue, setLocalValue] = useState(initialValue);
    const textareaRef = useRef(null);

    // Expose a method to sync localValue to parent state
    useImperativeHandle(ref, () => ({
      syncValue: () => {
        if (restrictSpecialCharactersMultilingual(localValue) || localValue === "") {
          setTranslations((prev) => {
            if (prev[sentenceId] === localValue) return prev;
            const next = { ...prev, [sentenceId]: localValue };
            translationsStateRef.current = next;
            return next;
          });
          setEditedSentences((prev) => {
            const s = new Set(prev);
            s.add(sentenceId);
            editedStateRef.current = s;
            return s;
          });
        }
      },
    }));

    // Sync local value with initial value when it changes externally
    useEffect(() => {
      setLocalValue(initialValue);
    }, [initialValue]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      if (restrictSpecialCharactersMultilingual(newValue) || newValue === "") {
        setLocalValue(newValue);
      } else {
        setPopup({
          isOpen: true,
          type: "error",
          message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
        });
        e.target.blur();
      }
    };

    const handleBlur = () => {
      // Update parent state on blur
      if (restrictSpecialCharactersMultilingual(localValue) || localValue === "") {
        setTranslations((prev) => {
          if (prev[sentenceId] === localValue) return prev;
          const next = { ...prev, [sentenceId]: localValue };
          translationsStateRef.current = next;
          return next;
        });
        setEditedSentences((prev) => {
          const s = new Set(prev);
          s.add(sentenceId);
          editedStateRef.current = s;
          return s;
        });
      }
    };

    const handleKeyDown = (e) => {
      const allowed = /^[a-zA-Z0-9\s()?]$/;
      if (!allowed.test(e.key) && e.key.length === 1) {
        e.preventDefault();
        setPopup({
          isOpen: true,
          type: "error",
          message: "Special characters are not allowed. Use only letters, numbers, spaces, commas, parentheses, and question marks.",
        });
      }
    };

    return fileStatus !== "DEV_COMPLETED" ? (
      <textarea
        ref={textareaRef}
        className="w-full max-w-[350px] min-h-[40px] border border-gray-300 rounded-lg p-2 text-sm font-['Semantic/Strong'] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
        value={localValue}
        onChange={handleChange}
        onKeyUp={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={isFileAlreadyValidated || finalCommitCompleted}
        onPaste={(e) => {
          e.preventDefault();
          return false;
        }}
      />
    ) : (
      <span>{initialValue}</span>
    );
  }
);

const SentenceValidationTable = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [validationStatus, setValidationStatus] = useState({});
  const [comments, setComments] = useState({});
  const [allSentences, setAllSentences] = useState([]);
  const [validated, setValidated] = useState({});
  const [translations, setTranslations] = useState({});
  const [editedSentences, setEditedSentences] = useState(new Set());
  const [validatedSentences, setValidatedSentences] = useState(new Set());
  const [finalCommitCompleted, setFinalCommitCompleted] = useState(false);
  const [clickReject, setClickReject] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [validationTrigger, setValidationTrigger] = useState(0);
  const [actionToConfirm, setActionToConfirm] = useState(null);
  const inputRefs = useRef({});
  const translationsStateRef = useRef({});
  const editedStateRef = useRef(new Set());
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });
  const navigate = useNavigate();
  const { targetLanguageName } = useSelector((store) => store.fileLanguage);
  const { fileName } = useSelector((store) => store.fileMeta);

  const { selectedFile, selectedAssignedFileId, isFileValidated, fileStatus } = useSelector((state) => state.validator);
  const isFileAlreadyValidated = selectedFile && (selectedFile.vStatus === "VALIDATED" || selectedFile.vStatus === "SUBMITTED_TO_GS" || selectedFile.vStatus === "DEV_COMPLETED");
  const { token, userId } = useSelector((state) => state.auth);

  const isSubmittedToGS = selectedFile && selectedFile.vStatus === "SUBMITTED_TO_GS";
  const isFileAlreadyValidatedRef = useRef(isFileAlreadyValidated);
  const handleValidateRef = useRef(null);
  const handleRejectRef = useRef(null);

  // Render popup for warnings
  const renderPopup = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-200">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-[calc(100vw-2rem)] sm:w-4/5 md:w-3/4 max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 z-10">
          <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
            <span className="text-[#ec7026] shadow-sm">{popup.type === "success" ? "Success" : "Error"}</span> Message
          </h2>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-sm font-['Semantic/Strong'] text-gray-800">{popup.message}</div>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 bg-white flex justify-end">
          <button
            onClick={() => setPopup({ isOpen: false, type: "", message: "" })}
            className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            OK
          </button>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    translationsStateRef.current = translations;
  }, [translations]);

  useEffect(() => {
    editedStateRef.current = editedSentences;
  }, [editedSentences]);

  useEffect(() => {
    isFileAlreadyValidatedRef.current = isFileAlreadyValidated;
    console.log("[isFileAlreadyValidatedRef] Updated:", isFileAlreadyValidatedRef.current, "finalCommitCompleted:", finalCommitCompleted, "selectedFile.vStatus:", selectedFile?.vStatus);
  }, [isFileAlreadyValidated, finalCommitCompleted, selectedFile]);

  // Fetch sentences for the selected file
  useEffect(() => {
    const fetchSentences = async () => {
      if (!selectedFile || !selectedFile.fileId) return;
      console.log("[fetchSentences] Triggered with reloadTrigger:", reloadTrigger, "clickReject:", clickReject, "fileId:", selectedFile.fileId);
      setLoading(true);
      try {
        const url = clickReject
          ? `${BASE_URL}api/validatorUser/rejectedSentences?fileId=${selectedFile.fileId}&validatorUserId=${selectedFile.validatorUserId}&sourceLangId=${selectedFile.sourceId}&targetLangId=${selectedFile.targetId}`
          : `${BASE_URL}api/validatorUser/approvedSentences?fileId=${selectedFile.fileId}&validatorUserId=${selectedFile.validatorUserId}&sourceLangId=${selectedFile.sourceId}&targetLangId=${selectedFile.targetId}`;
        const data = await apiClient(url, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "*/*",
          },
          credentials: "include",
        }, navigate);
        if (data.status === "success" && Array.isArray(data.data)) {
          console.log("[fetchSentences] Fetched sentences:", data.data);
          const sortedSentences = [...data.data];
          setAllSentences(sortedSentences);
          const initialValidated = new Set();
          sortedSentences.forEach((sentence) => {
            if (sentence.validationStatus === "VALIDATED") {
              initialValidated.add(sentence.sentenceId);
            }
            inputRefs.current[sentence.sentenceId] = React.createRef();
          });
          setValidatedSentences(initialValidated);
          setEditedSentences(new Set());
        } else {
          console.log("[fetchSentences] No sentences or invalid response:", data);
          setAllSentences([]);
        }
      } catch (error) {
        console.error("[fetchSentences] Error:", error);
        setAllSentences([]);
        toast.error("Failed to fetch sentences", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
          className: "rounded-lg shadow-md border border-red-200",
          style: { backgroundColor: "#dc3545", color: "#ffffff" },
        });
      } finally {
        setLoading(false);
      }
    };
    fetchSentences();
  }, [selectedFile, clickReject, reloadTrigger, token]);

  // Compute paginated data
  const paginatedSentences = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return allSentences.slice(start, end);
  }, [allSentences, pageIndex, pageSize]);

  // Update hasNextPage for client-side pagination
  useEffect(() => {
    setHasNextPage((pageIndex + 1) * pageSize < allSentences.length);
  }, [allSentences, pageIndex, pageSize]);

  // Initialize translations when sentences are loaded
  useEffect(() => {
    const initialTranslations = {};
    allSentences.forEach((s) => {
      initialTranslations[s.sentenceId] = s.validatedTextGs ?? s.translatedTextGs ?? "";
    });
    setTranslations(initialTranslations);
  }, [allSentences]);

  // Initialize validation status for all sentences
  useEffect(() => {
    const initialStatus = {};
    const initialComments = {};
    allSentences.forEach((sentence) => {
      initialStatus[sentence.sentenceDetailsId] = sentence.validationStatus || "Pending";
      initialComments[sentence.sentenceDetailsId] = sentence.validationComment || "";
    });
    setValidationStatus(initialStatus);
    setComments(initialComments);
  }, [allSentences]);

  // Initialize validated status when sentences are loaded
  useEffect(() => {
    const initialValidated = {};
    allSentences.forEach((sentence) => {
      if (sentence.validationStatus === "VALIDATED") {
        initialValidated[sentence.sentenceDetailsId] = true;
      }
    });
    setValidated(initialValidated);
  }, [allSentences]);

  // Trigger useEffect when validation is initiated
  useEffect(() => {
    setAllSentences((prev) =>
      prev.map((sentence) =>
        validatedSentences.has(sentence.sentenceId)
          ? {
            ...sentence,
            validationStatus: "VALIDATED",
            validatedTextGs: translationsStateRef.current[sentence.sentenceId] ?? sentence.validatedTextGs,
          }
          : sentence
      )
    );
  }, [validatedSentences]);

  // Validate a single sentence
  const handleValidate = async (sentence, syncValueCallback) => {
    if (isFileAlreadyValidated || finalCommitCompleted) {
      console.log("[handleValidate] Blocked: isFileAlreadyValidated:", isFileAlreadyValidated, "finalCommitCompleted:", finalCommitCompleted);
      return;
    }
    // Sync the textarea value before validating
    if (syncValueCallback) {
      syncValueCallback();
    }
    setValidationTrigger((prev) => prev + 1);
    try {
      const latest = translationsStateRef.current[sentence.sentenceId] ?? sentence.validatedTextGs ?? sentence.translatedTextGs ?? "";
      const requestBody = {
        fileId: selectedFile.fileId,
        sentenceId: sentence.sentenceId,
        targetLanguageId: sentence.targetLanguageId,
        translatedText: latest,
      };
      const result = await apiClient(BASE_URL + `api/validatorUser/validate?validatorUserId=${selectedFile.validatorUserId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(requestBody),
      }, navigate);
      if (result.status === "success") {
        toast.success("Validation updated successfully", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
          className: "rounded-lg shadow-md border border-green-200",
          style: { backgroundColor: "#10B981", color: "#ffffff" },
        });
        setEditedSentences((prev) => {
          const s = new Set(prev);
          s.delete(sentence.sentenceId);
          return s;
        });
        setValidatedSentences((prev) => new Set([...prev, sentence.sentenceId]));
        setValidated((prev) => ({ ...prev, [sentence.sentenceDetailsId]: true }));
        setAllSentences((prev) =>
          prev.map((s) =>
            s.sentenceId === sentence.sentenceId
              ? { ...s, validationStatus: "VALIDATED" }
              : s
          )
        );
      } else {
        setValidatedSentences((prev) => {
          const s = new Set(prev);
          s.delete(sentence.sentenceId);
          return s;
        });
        toast.error(result.message || "Validation failed", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
          className: "rounded-lg shadow-md border border-red-200",
          style: { backgroundColor: "#dc3545", color: "#ffffff" },
        });
      }
    } catch {
      setValidatedSentences((prev) => {
        const s = new Set(prev);
        s.delete(sentence.sentenceId);
        return s;
      });
      toast.error("Validation failed", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
        className: "rounded-lg shadow-md border border-red-200",
        style: { backgroundColor: "#dc3545", color: "#ffffff" },
      });
    }
  };

  const handleReject = async (sentence) => {
    if (isFileAlreadyValidated || finalCommitCompleted) {
      console.log("[handleReject] Blocked: isFileAlreadyValidated:", isFileAlreadyValidated, "finalCommitCompleted:", finalCommitCompleted);
      return;
    }
    try {
      const result = await apiClient(BASE_URL + `api/validatorUser/rejectSentence?sentenceId=${sentence.sentenceId}&validatorUserId=${userId}&sourceId=2&targetId=${sentence.targetLanguageId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        credentials: "include",
      }, navigate);
      console.log("[handleReject] API Response:", result);
      if (result.status === "success") {
        toast.success("Sentence rejected successfully", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
          className: "rounded-lg shadow-md border border-green-200",
          style: { backgroundColor: "#10B981", color: "#ffffff" },
        });
        setReloadTrigger((prev) => prev + 1);
      } else {
        toast.error(result.message || "Sentence rejection failed", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
          className: "rounded-lg shadow-md border border-red-200",
          style: { backgroundColor: "#dc3545", color: "#ffffff" },
        });
      }
    } catch (error) {
      console.error("[handleReject] Error:", error);
      toast.error("Sentence rejection failed", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
        className: "rounded-lg shadow-md border border-red-200",
        style: { backgroundColor: "#dc3545", color: "#ffffff" },
      });
    }
  };

  // Final commit handler
  const handleFinalCommit = async () => {
    if (isFileAlreadyValidated || finalCommitCompleted) {
      console.log("[handleFinalCommit] Blocked: isFileAlreadyValidated:", isFileAlreadyValidated, "finalCommitCompleted:", finalCommitCompleted);
      return;
    }
    if (!selectedFile || !selectedFile.fileId || !selectedFile.sourceId || !selectedFile.targetId || !selectedFile.validatorUserId) {
      toast.error("Missing file or validator information", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
        className: "rounded-lg shadow-md border border-red-200",
        style: { backgroundColor: "#dc3545", color: "#ffffff" },
      });
      return;
    }
    setLoading(true);
    try {
      const requestBody = {
        fileId: selectedFile.fileId,
        sourceLanguageId: selectedFile.sourceId,
        targetLanguageId: selectedFile.targetId,
        validatorUserId: parseInt(selectedFile.validatorUserId),
      };
      const result = await apiClient(BASE_URL + "api/validatorUser/mark-validation-completed", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify(requestBody),
      }, navigate);
      console.log("[handleFinalCommit] API Response:", result);
      if (result.success) {
        toast.success(result.message || "Validation work marked as completed successfully", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
          className: "rounded-lg shadow-md border border-green-200",
          style: { backgroundColor: "#10B981", color: "#ffffff" },
        });
        setFinalCommitCompleted(true);
        setReloadTrigger((prev) => prev + 1);
        dispatch({
          type: "validator/updateFileStatus",
          payload: { fileId: selectedFile.fileId, vStatus: "VALIDATED" },
        });
      } else {
        toast.error(result || "Failed to mark validation as completed", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
          className: "rounded-lg shadow-md border border-red-200",
          style: { backgroundColor: "#dc3545", color: "#ffffff" },
        });
      }
    } catch (error) {
      console.error("[handleFinalCommit] Error:", error);
      toast.error(error.message, {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
        className: "rounded-lg shadow-md border border-red-200",
        style: { backgroundColor: "#dc3545", color: "#ffffff" },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleValidateRef.current = handleValidate;
  }, [handleValidate]);

  useEffect(() => {
    handleRejectRef.current = handleReject;
  }, [handleReject]);

  const handleFinalCommitClick = () => setShowConfirmDialog(true);

  const handleConfirmFinalCommit = () => {
    setShowConfirmDialog(false);
    handleFinalCommit();
  };

  const handleCancelFinalCommit = () => setShowConfirmDialog(false);

  const handleAllSentencesClick = () => {
    if (!clickReject) return;
    setClickReject(false);
    setReloadTrigger((prev) => prev + 1);
  };

  const handleRejectedSentencesClick = () => {
    if (clickReject) return;
    setClickReject(true);
    setReloadTrigger((prev) => prev + 1);
  };

  const handlePullBack = async ({ row }) => {
    if (isFileAlreadyValidated || finalCommitCompleted) {
      console.log("[handlePullBack] Blocked: isFileAlreadyValidated:", isFileAlreadyValidated, "finalCommitCompleted:", finalCommitCompleted);
      return;
    }
    console.log("Pulling back sentence:", row.original);
    try {
      const result = await apiClient(
        `${BASE_URL}api/validatorUser/pullbackSentence?sentenceId=${row.original.sentenceId}&validatorUserId=${userId}&sourceId=${row.original.sourceLanguageId}&targetId=${row.original.targetLanguageId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          credentials: "include",
        },
        navigate
      );
      if (result.status === "success") {
        toast.success("Sentence pulled back successfully", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
          className: "rounded-lg shadow-md border border-green-200",
          style: { backgroundColor: "#10B981", color: "#ffffff" },
        });
        setReloadTrigger((prev) => prev + 1);
      } else {
        toast.error(result.message || "Failed to pull back sentence", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
          className: "rounded-lg shadow-md border border-red-200",
          style: { backgroundColor: "#dc3545", color: "#ffffff" },
        });
      }
    } catch (error) {
      console.error("[handlePullBack] Error:", error);
      toast.error(error.message, {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
        className: "rounded-lg shadow-md border border-red-200",
        style: { backgroundColor: "#dc3545", color: "#ffffff" },
      });
    }
  };

  const allSentencesValidated = allSentences.length > 0 && allSentences.every((sentence) => sentence.validationStatus === "VALIDATED" || sentence.validationStatus === "Edited");

  const getStatusColor = (status) => {
    switch (status) {
      case "VALIDATED":
        return "bg-green-100 text-green-800";
      case "Edited":
      case "IN_PROGRESS":
        return "bg-blue-100 text-blue-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const allSentencesColumns = useMemo(
    () => [
      {
        id: "serial_number",
        size: 100,
        header: () => "Sr. No.",
        cell: ({ row }) => <div className="text-center">{pageIndex * pageSize + row.index + 1}</div>,
        cellClassName: "text-center",
      },
      {
        id: "sourceText",
        header: () => "CDAC Source Text",
        accessorKey: "cdacSentenceText",
        cell: ({ row }) => <span>{row.original.cdacSentenceText}</span>,
      },
      {
        id: "gstranslatedText",
        header: () => `GS Translated Text (${targetLanguageName})`,
        accessorKey: "translatedTextGs",
        cell: ({ row }) => <span>{row.original.translatedTextGs}</span>,
      },
      {
        id: "translatedText",
        size: 800,
        header: () => `GS Validated Text (${targetLanguageName})`,
        accessorKey: "validatedTextGs",
        cell: ({ row }) => {
          const sentenceId = row.original.sentenceId;
          const inputRef = inputRefs.current[sentenceId] || React.createRef();
          inputRefs.current[sentenceId] = inputRef;
          const initialValue = translations[sentenceId] ?? row.original.validatedTextGs ?? row.original.translatedTextGs ?? "";
          return (
            <ValidatedTextCell
              ref={inputRef}
              sentenceId={sentenceId}
              initialValue={initialValue}
              fileStatus={fileStatus}
              isFileAlreadyValidated={isFileAlreadyValidated}
              finalCommitCompleted={finalCommitCompleted}
              setTranslations={setTranslations}
              setEditedSentences={setEditedSentences}
              translationsStateRef={translationsStateRef}
              editedStateRef={editedStateRef}
              setPopup={setPopup}
            />
          );
        },
      },
      {
        id: "validationStatus",
        header: () => "Validation Status",
        size: 100,
        accessorKey: "validationStatus",
        cell: ({ row }) => {
          const isEdited = editedStateRef.current.has(row.original.sentenceId);
          const status = isEdited ? "Edited" : (row.original.validationStatus || "Pending");

          return (
            <span
              className={`inline-block px-2 py-1 rounded-full text-xs font-['Semantic/Strong'] text-center ${getStatusColor(status)}`}
              style={{ minWidth: "120px" }}
            >
              {status}
            </span>
          );
        },
        cellClassName: "text-center",
      }
      ,
      {
        id: "action",
        header: () => "Action",
        cell: ({ row }) => {
          if (clickReject) return null;
          const canValidate =
            (["Edited", "IN_PROGRESS"].includes(row.original.validationStatus) ||
              editedStateRef.current.has(row.original.sentenceId)) &&
            !(isFileAlreadyValidated || finalCommitCompleted);
          return (
            <div className="flex flex-col gap-2 justify-center">
              <button
                className={`px-3 py-1 rounded text-sm font-['Semantic/Strong'] rounded-lg text-white ${canValidate ? "bg-[#ec7026] hover:bg-[#d85d1a]" : "bg-gray-400 cursor-not-allowed"}`}
                onClick={() => {
                  if (canValidate) {
                    setActionToConfirm({
                      type: "Validate",
                      sentence: row.original,
                      syncValue: () => inputRefs.current[row.original.sentenceId]?.current?.syncValue(),
                    });
                    setShowConfirmDialog(true);
                  }
                }}
                disabled={!canValidate}
              >
                {canValidate ? "Validate" : "Validated"}
              </button>
              <button
                className={`px-3 py-1 rounded text-sm font-['Semantic/Strong'] rounded-lg text-white ${!(isFileAlreadyValidated || finalCommitCompleted) ? "bg-red-500 hover:bg-red-600" : "bg-gray-400 cursor-not-allowed"}`}
                onClick={() => {
                  if (!(isFileAlreadyValidated || finalCommitCompleted)) {
                    setActionToConfirm({ type: "Reject", sentence: row.original });
                    setShowConfirmDialog(true);
                  }
                }}
                disabled={isFileAlreadyValidated || finalCommitCompleted}
              >
                Reject
              </button>
            </div>
          );
        },
        cellClassName: "text-center",
      },
    ],
    [isFileAlreadyValidated, finalCommitCompleted, pageIndex, pageSize, translations, fileStatus, setTranslations, setEditedSentences, setPopup]
  );

  const rejectedSentencesColumns = useMemo(
    () => [
      {
        id: "serial_number",
        header: () => "Sr. No.",
        cell: ({ row }) => <div className="text-center">{pageIndex * pageSize + row.index + 1}</div>,
        cellClassName: "text-center",
      },
      {
        id: "cdacSentenceText",
        header: () => "CDAC Sentence Text",
        accessorKey: "cdacSentenceText",
        cell: ({ row }) => <div className="max-w-md">{row.original.cdacSentenceText || "N/A"}</div>,
      },
      {
        id: "translatedTextGs",
        header: () => `GS Translated Text (${targetLanguageName})`,
        accessorKey: "translatedTextGs",
        cell: ({ row }) => <div className="max-w-md">{row.original.translatedTextGs || "N/A"}</div>,
      },
      {
        id: "validatedTextGs",
        header: () => `GS Validated Text  (${targetLanguageName})`,
        accessorKey: "validatedTextGs",
        cell: ({ row }) => <div className="max-w-md">{row.original.validatedTextGs || "N/A"}</div>,
      },
      {
        id: "validationStatus",
        header: () => "Validation Status",
        accessorKey: "validationStatus",
        cell: ({ row }) => (
          <span className={`px-2 py-1 rounded-full text-sm font-['Semantic/Strong'] ${getStatusColor(row.original.validationStatus)}`}>
            {row.original.validationStatus || "REJECTED"}
          </span>
        ),
        cellClassName: "text-center",
      },
      {
        id: "pullbackBtn",
        header: () => "Pull Back",
        cell: ({ row }) => (
          <button
            className={`px-3 py-1 rounded text-sm font-['Semantic/Strong'] text-white ${!(isFileAlreadyValidated || finalCommitCompleted) ? "bg-red-500 hover:bg-red-600" : "bg-gray-400 cursor-not-allowed"}`}
            onClick={() => handlePullBack({ row })}
            disabled={isFileAlreadyValidated || finalCommitCompleted}
          >
            Pull
          </button>
        ),
        cellClassName: "text-center",
      },
    ],
    [isFileAlreadyValidated, finalCommitCompleted, pageIndex, pageSize]
  );

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="flex-1">
        <div className="w-full p-4 sm:p-6 lg:p-8">
          {!selectedAssignedFileId ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-gray-500 text-sm font-['Semantic/Strong']">
                Please select a file to validate.
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 sm:px-6 lg:px-8 py-4">
                <h2 className="text-xl font-['Semantic/Strong'] text-center font-bold mb-2">
                  File Name: <span className="text-3xl font-['Semantic/Strong'] text-[#ec7026] underline font-bold">{fileName}</span>
                </h2>

                {isFileAlreadyValidated && (
                  <div className="bg-blue-100 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-4 font-['Semantic/Strong']">
                    <p>
                      {isSubmittedToGS
                        ? "This file has been submitted to GS Admin. All actions are disabled."
                        : "This file has been validated and committed. All actions are disabled."}
                    </p>
                  </div>
                )}
                <div className="flex items-center mb-4">
                  <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${!clickReject ? "bg-[#ec7026] text-white" : "bg-white text-[#ec7026] hover:bg-gray-50"} transition-colors duration-200`}
                      onClick={handleAllSentencesClick}
                    >
                      All Sentences
                    </button>
                    <button
                      className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${clickReject ? "bg-[#ec7026] text-white" : "bg-white text-[#ec7026] hover:bg-gray-50"} transition-colors duration-200`}
                      onClick={handleRejectedSentencesClick}
                    >
                      Rejected Sentences
                    </button>
                  </div>
                </div>
                {loading ? (
                  <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                  </div>
                ) : paginatedSentences.length === 0 ? (
                  <div className="p-6">
                    {clickReject ? (
                      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg font-['Semantic/Strong']">
                        <p>No rejected sentences found.</p>
                        <p className="mt-2 text-sm">Please try again later or contact support.</p>
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 font-['Semantic/Strong']">
                        No sentences available
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full overflow-x-auto">
                    <GenericTable
                      data={paginatedSentences}
                      columns={clickReject ? rejectedSentencesColumns : allSentencesColumns}
                      showPagination={true}
                      manualPagination={true}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      hasNextPage={hasNextPage}
                      onPaginationChange={({ pageIndex, pageSize }) => {
                        setPageIndex(pageIndex);
                        setPageSize(pageSize);
                      }}
                      emptyMessage={clickReject ? "No rejected sentences" : "No sentences available"}
                    />
                  </div>
                )}
                {!clickReject && allSentences.length > 0 && (
                  <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 flex justify-end">
                    <div className="flex flex-col items-end">
                      <button
                        className={`px-6 py-2 rounded-lg text-sm font-['Semantic/Strong'] text-white ${!finalCommitCompleted && !isFileAlreadyValidated ? "bg-[#ec7026] hover:bg-[#d85d1a]" : "bg-gray-400 cursor-not-allowed"} transition-colors duration-200`}
                        onClick={handleFinalCommitClick}
                        disabled={!allSentencesValidated || isFileValidated || finalCommitCompleted || isFileAlreadyValidated}
                      >
                        {loading ? "Processing..." : finalCommitCompleted || isFileValidated || isFileAlreadyValidated ? "Final Commit Completed" : "Final Commit"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {showConfirmDialog && actionToConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-['Semantic/Strong'] font-semibold mb-4">Confirm Action</h3>
                <p className="text-gray-600 font-['Semantic/Strong'] mb-6">
                  Are you sure you want to {actionToConfirm.type.toLowerCase()} the sentence "{actionToConfirm.sentence.cdacSentenceText}"? This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    className="px-4 py-2 text-sm font-['Semantic/Strong'] text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                    onClick={() => {
                      setShowConfirmDialog(false);
                      setActionToConfirm(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 text-sm font-['Semantic/Strong'] text-white bg-[#ec7026] rounded hover:bg-[#d85d1a]"
                    onClick={() => {
                      setShowConfirmDialog(false);
                      if (actionToConfirm.type === "Validate") {
                        handleValidateRef.current && handleValidateRef.current(actionToConfirm.sentence, actionToConfirm.syncValue);
                      } else if (actionToConfirm.type === "Reject") {
                        handleRejectRef.current && handleRejectRef.current(actionToConfirm.sentence);
                      }
                      setActionToConfirm(null);
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
          {showConfirmDialog && !actionToConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-['Semantic/Strong'] font-semibold mb-4">Confirm Final Commit</h3>
                <p className="text-gray-600 font-['Semantic/Strong'] mb-6">
                  Are you sure you want to submit all validated sentences? This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    className="px-4 py-2 text-sm font-['Semantic/Strong'] text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                    onClick={handleCancelFinalCommit}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 text-sm font-['Semantic/Strong'] text-white bg-[#ec7026] rounded hover:bg-[#d85d1a]"
                    onClick={handleConfirmFinalCommit}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
          {popup.isOpen && renderPopup()}
        </div>
      </div>
    </div>
  );
};

export default SentenceValidationTable;